<template>
  <form v-on:submit.prevent="submit" v-on:change="changeHandler">
    <div class="card-body">
      <div class="card-body-overlay" v-if="!thirdParty">
        Please complete the "TP Details" section before the TP Vehicle details.
      </div>

      <div class="col-lg-6">
        <vehicle-lookup
          v-on:vrm="setRegistration"
          v-on:vehicle="setVehicle"
          label="VRN"
          helper="Please enter the vehicle registration number for the third party"
          :registration="details.registration"
        ></vehicle-lookup>

        <selectlist
          :options="types"
          v-model="details.type_id"
          label="Vehicle Type"
          name="vehicle_type"
          helper="Please select the type of vehicle for the third party"
          placeholder="Please Select"
        ></selectlist>

        <selectlist
          :options="makes"
          v-model="details.make_id"
          label="Vehicle Make"
          placeholder="Please Select"
          helper="Please select the vehicle make for the third party"
          name="make"
        ></selectlist>

        <inputbox
          v-model="details.model"
          name="model"
          label="Vehicle Model"
          helper="Please enter the vehicle model for the third party"
        ></inputbox>

        <selectlist
          :options="transmissions"
          v-model="details.transmission_id"
          name="transmission"
          helper="Please select the transmission type for the vehicle"
          placeholder="Please Select"
          label="Vehicle Transmission"
        ></selectlist>
      </div>
      <div class="col-lg-6">
        <div
          class="form-group has-helper"
          :class="{ 'has-error': details.errors.has('registered_at') }"
        >
          <div class="col-lg-4">
            <label for="registered_at" class="control-label"
              >Registration Date:</label
            >
            <p class="help-block">
              Please enter the date the vehicle was registered
            </p>
          </div>
          <div class="col-lg-8">
            <div class="input-group">
              <datepicker
                :disabled="disabled"
                format="dd/MM/yyyy"
                v-bind:value="details.registered_at"
                input-class="form-control"
                v-on:selected="setRegistrationDate"
                v-on:input="changeHandler"
                placeholder="dd/mm/yyyy"
              ></datepicker>
              <span
                class="text-danger"
                v-text="details.errors.get('registered_at')"
                v-if="details.errors.has('registered_at')"
              ></span>
              <div class="input-group-append">
                <span class="input-group-text">
                  {{ age }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <inputbox
          v-model="details.colour"
          name="vehicle_colour"
          label="Vehicle Colour"
          helper="Please enter the colour of the third party's vehicle"
        ></inputbox>

        <inputbox
          v-model="details.engine_capacity"
          name="engine_capacity"
          label="Engine Capacity"
          helper="Please enter the engine capacity of the third party's vehicle"
        ></inputbox>
      </div>
      <div class="col-lg-12">
        <fieldset>
          <legend>
            Damage Details
            <small>- Enter the damage details for the vehicle here</small>
          </legend>
          <div class="row">
            <div class="col-md-4">
              <div class="damage-key">
                <div class="colour severe"></div>
                <div class="damage-label">Severe</div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="damage-key">
                <div class="colour major"></div>
                <div class="damage-label">Major</div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="damage-key">
                <div class="colour minor"></div>
                <div class="damage-label">Minor</div>
              </div>
            </div>
          </div>
          <div class="col-12 text-center">
            <left
              v-on:updated="updateLeftDamage"
              :front="damage.left_front"
              :frontDoor="damage.left_front_door"
              :rearDoor="damage.left_rear_door"
              :rear="damage.left_rear"
              :frontWheel="damage.left_front_wheel"
              :rearWheel="damage.left_rear_wheel"
            ></left>
          </div>
          <div class="row">
            <div class="col-12 main-images">
              <div class="col-lg-4 col-lg-push-4 text-center roof">
                <roof
                  v-on:updated="updateRoofDamage"
                  :rearWindscreen="damage.rear_windscreen"
                  :roof="damage.roof"
                  :frontWindscreen="damage.front_windscreen"
                  :bonnet="damage.top_bonnet"
                ></roof>
              </div>
              <div class="col-lg-4 col-lg-pull-4 col-md-6 text-center rear">
                <rear
                  v-on:updated="updateRearDamage"
                  :bumper="damage.rear_bumper"
                  :boot="damage.rear_boot"
                ></rear>
              </div>
              <div class="col-lg-4 col-md-6 text-center front">
                <front
                  v-on:updated="updateFrontDamage"
                  :bumper="damage.front_bumper"
                  :bonnet="damage.front_bonnet"
                ></front>
              </div>
            </div>
          </div>
          <div class="col-12 text-center">
            <right
              v-on:updated="updateRightDamage"
              :front="damage.right_front"
              :frontDoor="damage.right_front_door"
              :rearDoor="damage.right_rear_door"
              :rear="damage.right_rear"
              :frontWheel="damage.right_front_wheel"
              :rearWheel="damage.right_rear_wheel"
            ></right>
          </div>
        </fieldset>
      </div>
    </div>
    <div class="card-footer text-right">
      <button
        class="btn btn-primary btn-raised"
        name="cancel"
        v-on:click.prevent="cancel"
      >
        <i class="icon icon-cancel"></i> Back
      </button>
      <button
        class="btn btn-primary btn-raised"
        name="save"
        v-on:click.prevent="submit"
      >
        <i class="icon icon-save"></i> Save
      </button>
    </div>
  </form>
</template>

<script>
import VehicleLookup from './../../../../../Generic/VehicleLookup.vue';
import Rear from './../../../../FirstNoticeOfLoss/Sections/Children/Car/Rear.vue';
import Roof from './../../../../FirstNoticeOfLoss/Sections/Children/Car/Roof.vue';
import Front from './../../../../FirstNoticeOfLoss/Sections/Children/Car/Front.vue';
import Left from './../../../../FirstNoticeOfLoss/Sections/Children/Car/Left.vue';
import Right from './../../../../FirstNoticeOfLoss/Sections/Children/Car/Right.vue';

export default {
  props: {
    incident: {
      type: String,
      required: true
    },

    thirdParty: {
      type: String
    },

    mode: {
      type: String
    }
  },

  data() {
    return {
      disabled: {
        from: new Date()
      },
      types: [],
      makes: [],
      transmissions: [],
      vehicle_identifier: null,
      damage_identifier: null,
      details: new window.Form({
        registration: null,
        type_id: null,
        make_id: null,
        model: null,
        transmission_id: null,
        registered_at: null,
        colour: null,
        engine_capacity: null
      }),
      damage: new window.Form({
        rear_bumper: 0,
        rear_boot: 0,
        rear_windscreen: 0,
        roof: 0,
        front_windscreen: 0,
        top_bonnet: 0,
        front_bumper: 0,
        front_bonnet: 0,
        left_front_door: 0,
        left_front: 0,
        left_rear_door: 0,
        left_rear: 0,
        left_rear_wheel: 0,
        left_front_wheel: 0,
        right_front_door: 0,
        right_front: 0,
        right_rear_door: 0,
        right_rear: 0,
        right_front_wheel: 0,
        right_rear_wheel: 0
      }),
      data_entered: false,
      indicator: false
    };
  },

  components: { Rear, Roof, Front, Left, Right, VehicleLookup },

  computed: {
    age() {
      if (
        !this.details.registered_at ||
        this.details.registered_at === 'Invalid date'
      ) {
        return 'Please select a date';
      }

      let yearsOld = window.DateManager.differenceInYears(
        new Date(),
        this.details.registered_at
      ).toString();
      let split = yearsOld.split('.');
      let ageInYears = split[0];

      let monthsCalc = '0.' + split[1];
      let ageInMonths = Math.floor(
        parseFloat(parseFloat(12) * parseFloat(monthsCalc))
      );

      return ageInYears + ' Years ' + ageInMonths + ' Months';
    }
  },

  mounted() {
    this.mount();
  },

  methods: {
    mount() {
      window.Ajax.get(
        `/incidents/${this.incident}/third-parties/${this.thirdParty}`
      )
        .then(response => {
          if (response.data) {
            window.Ajax.get('/vehicle-types')
              .then(response => {
                this.types = response.data;
              })
              .catch(response => {});

            window.Ajax.get('/vehicle-makes')
              .then(response => {
                this.makes = response.data;
              })
              .catch(response => {});

            window.Ajax.get('/transmissions')
              .then(response => {
                this.transmissions = response.data;
              })
              .catch(response => {});

            this.getThirdPartyVehicle();
          }
        })
        .catch(error => {});
    },

    getThirdPartyVehicle() {
      window.Ajax.get(
        `/incidents/${this.incident}/third-parties/${this.thirdParty}/vehicle`
      )
        .then(response => {
          let vehicle = response.data;
          this.vehicle_identifier = vehicle.identifier;
          this.details.registration = vehicle.registration;
          this.details.type_id = vehicle.type_id;
          this.details.make_id = vehicle.make_id;
          this.details.model = vehicle.model;
          this.details.registered_at = window.DateManager.format(
            vehicle.registered_at
          );
          this.details.colour = vehicle.colour;
          this.details.engine_capacity = vehicle.engine_capacity;
          this.details.transmission_id = vehicle.transmission_id;

          window.Ajax.get(
            `/incidents/${this.incident}/third-parties/${this.thirdParty}/vehicle/${this.vehicle_identifier}/damage`
          )
            .then(response => {
              let data = response.data;
              this.damage_identifier = vehicle.identifier;

              this.updateCar(data);
            })
            .catch(error => {});
        })
        .catch(error => {});
    },

    updateFrontDamage(value) {
      let refined = {
        front_bumper: value.bumper,
        front_bonnet: value.bonnet
      };

      this.updateCar(refined);
      this.changeHandler();
    },

    updateRightDamage(value) {
      let refined = {
        right_front_door: value.frontDoor,
        right_front: value.frontBumper,
        right_rear_door: value.backDoor,
        right_rear: value.rearBumper,
        right_front_wheel: value.frontWheel,
        right_rear_wheel: value.rearWheel
      };

      this.updateCar(refined);
      this.changeHandler();
    },

    updateLeftDamage(value) {
      let refined = {
        left_front_door: value.frontDoor,
        left_front: value.frontBumper,
        left_rear_door: value.backDoor,
        left_rear: value.rearBumper,
        left_front_wheel: value.frontWheel,
        left_rear_wheel: value.rearWheel
      };

      this.updateCar(refined);
      this.changeHandler();
    },

    updateRoofDamage(value) {
      let refined = {
        rear_windscreen: value.rearWindscreen,
        roof: value.roof,
        front_windscreen: value.frontWindscreen,
        top_bonnet: value.bonnet
      };

      this.updateCar(refined);
      this.changeHandler();
    },

    updateRearDamage(value) {
      let refined = {
        rear_bumper: value.bumper,
        rear_boot: value.boot
      };

      this.updateCar(refined);
      this.changeHandler();
    },

    updateCar(data) {
      for (let property in data) {
        if (this.damage[property]) {
          this.damage[property] = data[property];
        }
      }
    },

    changeHandler() {
      if (!this.data_entered) {
        this.data_entered = true;
      }
      this.$emit('unsaved-data', this.data_entered);
    },

    submit() {
      window.toastify.info('Saving', "We're saving those details for you.");
      this.indicator = true;

      if (this.vehicle_identifier) {
        this.details
          .put(
            `/incidents/${this.incident}/third-parties/${this.thirdParty}/vehicle`
          )
          .then(response => {
            this.saveDamageDetails();
            this.indicator = false;
            // reset monitoring of form changes
            this.data_entered = false;
            this.$emit('unsaved-data', this.data_entered);
          })
          .catch(response => {
            this.indicator = false;
            window.toastify.error(
              'Error',
              'Sorry, there was an error saving the third party vehicle.'
            );
          });
      } else {
        this.details
          .post(
            `/incidents/${this.incident}/third-parties/${this.thirdParty}/vehicle`
          )
          .then(response => {
            this.vehicle_identifier = response.identifier;
            this.indicator = false;
            this.saveDamageDetails();
            // reset monitoring of form changes
            this.data_entered = false;
            this.$emit('unsaved-data', this.data_entered);
          })
          .catch(response => {
            this.indicator = false;
            window.toastify.error(
              'Error',
              'Sorry, there was an error saving the third party vehicle.'
            );
          });
      }
    },

    cancel() {
      window
        .swal({
          title: 'Are you sure?',
          text: 'You will lose any unsaved changes',
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes'
        })
        .then(confirmed => {
          if (confirmed) {
            this.$emit('close');
          }
          this.$router.back();
        })
        .catch(error => {});
    },

    saveDamageDetails() {
      if (this.vehicle_identifier) {
        if (this.damage_identifier) {
          this.damage
            .put(
              `/incidents/${this.incident}/third-parties/${this.thirdParty}/vehicle/${this.vehicle_identifier}/damage`
            )
            .then(response => {
              window.toastify.success(
                'Saved',
                "We've updated those details for you."
              );
            })
            .catch(error => {});
        } else {
          this.damage
            .post(
              `/incidents/${this.incident}/third-parties/${this.thirdParty}/vehicle/${this.vehicle_identifier}/damage`
            )
            .then(response => {
              window.toastify.success(
                'Saved',
                "We've updated those details for you."
              );
            })
            .catch(error => {});
        }
      }
    },

    setRegistration(registration) {
      this.details.registration = registration;
    },

    setVehicle(vehicle) {
      this.details.transmission_id = vehicle.transmission_type_id;
      this.details.make_id = vehicle.make_id;
      this.details.model = vehicle.model;
      this.details.registered_at = window.DateManager.format(
        vehicle.registered_at
      );
      this.details.colour = vehicle.colour;
      this.details.engine_capacity = vehicle.engine_capacity;
    },

    setRegistrationDate(value) {
      this.details.registered_at = value;
    }
  },

  watch: {
    $route(to, from) {
      this.mount();
    }
  }
};
</script>
